import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedDate } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';

import type { TooltipGame_anyGame } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  width: 300px;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-nd-50);
  text-align: center;
`;

const Row = styled.div<{ $reverse: boolean }>`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr max-content 1fr;
  gap: var(--unit);
  align-items: flex-start;
`;

const TeamWrapper = styled(Vertical)`
  gap: var(--unit);
  width: 100%;
  text-align: center;
`;

const Date = styled(BodyL)`
  white-space: nowrap;
`;

type Props = { anyGame: Nullable<TooltipGame_anyGame> };

export const TooltipGame = ({ anyGame }: Props) => {
  const { so5Fixture, date, homeTeam, awayTeam } = anyGame || {};
  const isUsSport = anyGame?.sport !== Sport.FOOTBALL;
  const home = (
    <TeamWrapper center>
      <TeamAvatar team={homeTeam} size={32} />
      <LabelM as="span">{homeTeam?.shortName}</LabelM>
    </TeamWrapper>
  );
  const away = (
    <TeamWrapper center>
      <TeamAvatar team={awayTeam} size={32} />
      <LabelM as="span">{awayTeam?.shortName}</LabelM>
    </TeamWrapper>
  );
  return (
    <Root center>
      <LabelS color="var(--c-nd-600)">
        {anyGame?.competition?.displayName}
      </LabelS>
      <Row $reverse={anyGame?.sport !== Sport.FOOTBALL}>
        {isUsSport ? away : home}
        <Vertical center gap={0.5}>
          <Date bold>
            <FormattedDate value={date} day="2-digit" month="short" />
          </Date>
          <Chip size="small">
            <FormattedDate value={date} timeStyle="short" />
          </Chip>
          {!!so5Fixture?.shortDisplayName && (
            <LabelM bold color="var(--c-nd-600)">
              {so5Fixture?.shortDisplayName}
            </LabelM>
          )}
        </Vertical>
        {isUsSport ? home : away}
      </Row>
    </Root>
  );
};

TooltipGame.fragments = {
  anyGame: gql`
    fragment TooltipGame_anyGame on AnyGameInterface {
      id
      date
      sport
      competition {
        slug
        displayName
      }
      homeTeam {
        slug
        shortName
        ...TeamAvatar_team
      }
      awayTeam {
        slug
        shortName
        ...TeamAvatar_team
      }
      so5Fixture {
        slug
        shortDisplayName
      }
    }
    ${TeamAvatar.fragments.team}
  ` as TypedDocumentNode<TooltipGame_anyGame>,
};
